import { useEffect, useState, type ReactNode } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api } from "@/lib/api";
import { centsToInput, formatCents, parseReaisInput } from "@/lib/format";
import type { FolhaItem } from "@/data/folha";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

/** O que o modal mostra e edita — linha PJ (FolhaItem) ou holerite mensal CLT. */
export type ItemAjustavel = Pick<
  FolhaItem,
  "id" | "nome" | "liquido" | "ajusteManual" | "ajusteMotivo" | "outrosDescontos"
>;

interface Props {
  open: boolean;
  onClose: () => void;
  folhaId: string;
  item: ItemAjustavel;
  onSalvo: () => void;
  /** Padrão: PATCH da linha PJ. O CLT usa PUT /clt/folhas/:id/ajustes/:colaboradorId. */
  endpoint?: string;
  metodo?: "patch" | "put";
  /** Observação extra (ex.: como o ajuste entra no holerite CLT). */
  nota?: ReactNode;
}

export const AjustarItemModal = ({
  open,
  onClose,
  folhaId,
  item,
  onSalvo,
  endpoint,
  metodo = "patch",
  nota,
}: Props) => {
  const [ajuste, setAjuste] = useState(centsToInput(item.ajusteManual));
  const [motivo, setMotivo] = useState(item.ajusteMotivo ?? "");
  const [outros, setOutros] = useState(centsToInput(item.outrosDescontos));
  const [salvando, setSalvando] = useState(false);

  useEffect(() => {
    if (!open) return;
    setAjuste(centsToInput(item.ajusteManual));
    setMotivo(item.ajusteMotivo ?? "");
    setOutros(centsToInput(item.outrosDescontos));
  }, [open, item]);

  const salvar = async () => {
    let ajusteReais: number;
    let outrosReais: number;
    try {
      ajusteReais = parseReaisInput(ajuste);
      outrosReais = parseReaisInput(outros);
    } catch {
      toast.error("Valor inválido. Use apenas números, ex: 250,00 ou -250,00");
      return;
    }

    if (outrosReais < 0) {
      toast.error("Outros descontos não pode ser negativo. Para creditar, use o ajuste manual.");
      return;
    }
    if (ajusteReais !== 0 && !motivo.trim()) {
      toast.error("Informe o motivo do ajuste manual");
      return;
    }

    setSalvando(true);
    try {
      await api[metodo](endpoint ?? `/folhas/${folhaId}/itens/${item.id}`, {
        ajusteManual: ajusteReais,
        ajusteMotivo: motivo.trim() || null,
        outrosDescontos: outrosReais,
      });
      toast.success("Linha ajustada");
      onSalvo();
      onClose();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Erro ao ajustar a linha");
    } finally {
      setSalvando(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Ajustar linha de {item.nome}</DialogTitle>
          <DialogDescription>
            Líquido calculado hoje: <strong>{formatCents(item.liquido)}</strong>. O ajuste é
            preservado quando a folha é recalculada.
            {nota && <span className="mt-1.5 block">{nota}</span>}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="ajuste-valor">Ajuste manual (R$)</Label>
            <Input
              id="ajuste-valor"
              value={ajuste}
              onChange={(e) => setAjuste(e.target.value)}
              inputMode="decimal"
              placeholder="0,00"
            />
            <p className="text-xs text-muted-foreground">
              Positivo credita, negativo debita. Ex: -250,00
            </p>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="ajuste-motivo">Motivo do ajuste</Label>
            <Input
              id="ajuste-motivo"
              value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
              maxLength={300}
              placeholder="Ex: bônus acordado fora da planilha"
            />
            <p className="text-xs text-muted-foreground">
              Fica registrado na auditoria e aparece no demonstrativo do colaborador.
            </p>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="ajuste-outros">Outros descontos (R$)</Label>
            <Input
              id="ajuste-outros"
              value={outros}
              onChange={(e) => setOutros(e.target.value)}
              inputMode="decimal"
              placeholder="0,00"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={salvando}>
            Cancelar
          </Button>
          <Button onClick={() => void salvar()} disabled={salvando}>
            {salvando && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Salvar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
